import type { ListingDetailDto, UpdateListingPayload } from "@/api/buyers";
import { parseListingNumber } from "../ListingEditModal";

/**
 * ONE config drives every Overview card's edit modal.
 *
 * <p>The shape follows <c>sections/buyerSectionConfig.ts</c>, which follows the
 * loan Profile tab's carried-intake sections. The reason is the same one it
 * gives: two hand-written renderings of one field set drift, and the one that
 * drifts is whichever nobody is looking at.</p>
 *
 * <p><b>The listing cards keep their own bespoke READ rendering</b> — Description
 * is a <c>whitespace-pre-wrap</c> block, Source carries a link and two read-only
 * rows, Financials formats currency — so this config drives the MODALS and is the
 * proof of coverage, not a card renderer. What it buys is the property that
 * matters: {@link listingSectionCoverage} is asserted against
 * <c>EMPTY_LISTING_FORM</c>, which is exactly the field set the whole-record edit
 * form used to carry, so no field can be dropped from the page by accident.</p>
 *
 * <p>🔴 <b>The keys are the WIRE keys.</b> Each <c>key</c> is simultaneously a
 * property of {@link ListingDetailDto} (what the card reads) and of
 * {@link UpdateListingPayload} (what the modal sends) — {@link ListingEditableKey}
 * is that intersection — so a typo or a rename fails the typecheck instead of
 * silently editing nothing.</p>
 */

/** A key a section may both render and patch. */
export type ListingEditableKey = Extract<
  keyof UpdateListingPayload,
  keyof ListingDetailDto
>;

export type ListingFieldType = "text" | "url" | "state" | "number" | "textarea";

export interface ListingFieldConfig {
  key: ListingEditableKey;
  label: string;
  type: ListingFieldType;
  /** Full-width in the modal's two-column grid. */
  wide?: boolean;
  hint?: string;
}

export type ListingSectionId =
  | "business"
  | "financials"
  | "description"
  | "notes"
  | "source";

export interface ListingSectionConfig {
  id: ListingSectionId;
  /** Must match the card's `DetailSection` title exactly — the modal is titled
   *  from it, and a modal named differently from the card that opened it is how
   *  a reader ends up unsure which thing they are editing. */
  title: string;
  /** Sentence shown at the top of the edit modal. */
  editDescription: string;
  fields: readonly ListingFieldConfig[];
}

/**
 * The five field sections, one per Overview card that edits listing columns.
 *
 * <p>The sixth card — Listing agent — is deliberately NOT here. It writes a
 * different record through a different endpoint (<c>PATCH /listings/agents/{id}</c>)
 * with its own shared-record consequences, so it has its own modal rather than a
 * row in a config whose whole contract is "these keys go to
 * <c>PATCH /listings/{id}</c>".</p>
 */
export const LISTING_SECTIONS: readonly ListingSectionConfig[] = [
  {
    id: "business",
    title: "Business",
    editDescription:
      "What this business is and where it operates. Leave a text field blank to clear it.",
    fields: [
      { key: "businessName", label: "Business name", type: "text", wide: true },
      { key: "headline", label: "Headline", type: "text", wide: true },
      { key: "industry", label: "Industry", type: "text" },
      { key: "city", label: "City", type: "text" },
      { key: "state", label: "State", type: "state" },
      { key: "zip", label: "ZIP", type: "text" },
      { key: "yearEstablished", label: "Year established", type: "number", hint: "e.g. 1998" },
      { key: "employeeCount", label: "Employees", type: "number" },
    ],
  },
  {
    id: "financials",
    title: "Financials",
    editDescription: "The figures this listing is being marketed on.",
    fields: [
      { key: "askingPrice", label: "Asking price", type: "number" },
      { key: "cashFlow", label: "Cash flow", type: "number" },
      { key: "revenue", label: "Revenue", type: "number" },
      { key: "inventory", label: "Inventory", type: "number" },
      { key: "realEstateValue", label: "Real estate", type: "number" },
    ],
  },
  {
    id: "description",
    title: "Description",
    editDescription: "The listing's own description of the business.",
    fields: [{ key: "description", label: "Description", type: "textarea", wide: true }],
  },
  {
    id: "notes",
    title: "Notes on file",
    editDescription:
      "The note field on this listing's record. Team discussion belongs on the Notes tab.",
    fields: [{ key: "notes", label: "Notes on file", type: "textarea", wide: true }],
  },
  {
    id: "source",
    title: "Source",
    editDescription:
      "Where this listing came from. The dedupe key and the date it was added are derived and cannot be edited.",
    fields: [
      { key: "marketplaceName", label: "Marketplace", type: "text" },
      { key: "listingReference", label: "Listing reference", type: "text" },
      { key: "rawUrl", label: "Listing URL", type: "url", wide: true },
    ],
  },
];

export function listingSection(id: ListingSectionId): ListingSectionConfig {
  const found = LISTING_SECTIONS.find((s) => s.id === id);
  // Unreachable via the type, but a thrown error beats rendering an empty modal
  // that looks like a section with no fields in it.
  if (!found) throw new Error(`Unknown listing section: ${id}`);
  return found;
}

/** Every key the five field sections between them cover, in declaration order. */
export function listingSectionCoverage(): ListingEditableKey[] {
  return LISTING_SECTIONS.flatMap((s) => s.fields.map((f) => f.key));
}

/**
 * The FIGURES, which is the set the write contract cannot clear.
 *
 * <p>Five of them are on Financials and two — year established and employee
 * count — are on Business, which is why the "clearing a figure will not clear
 * it" caption has to be driven from the section rather than hard-coded onto one
 * modal. A caption on Financials alone would leave two fields silently
 * dishonest.</p>
 */
export function sectionHasFigures(section: ListingSectionConfig): boolean {
  return section.fields.some((f) => f.type === "number");
}

/**
 * Every value the form holds is a STRING, because every input returns one.
 *
 * <p>The conversion happens once, on the way out, in
 * {@link buildListingSectionPatch}.</p>
 */
export type ListingSectionForm = Record<string, string>;

export function hydrateListingSection(
  section: ListingSectionConfig,
  listing: ListingDetailDto,
): ListingSectionForm {
  const form: ListingSectionForm = {};
  for (const field of section.fields) {
    const value = listing[field.key];
    form[field.key] = value === null || value === undefined ? "" : String(value);
  }
  return form;
}

/** A typed figure that does not parse is reported, never silently dropped. */
export function listingSectionNumberErrors(
  section: ListingSectionConfig,
  form: ListingSectionForm,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of section.fields) {
    if (field.type !== "number") continue;
    const raw = (form[field.key] ?? "").trim();
    if (raw && parseListingNumber(raw) === undefined) {
      errors[field.key] = "Enter a number.";
    }
  }
  return errors;
}

/**
 * Turn one section's form into the narrowest patch that expresses it.
 *
 * <p>Only this section's keys are sent. Omitted means unchanged, which is exactly
 * what a section edit means about every field it does not contain — so a
 * specialist correcting a marketplace name cannot accidentally re-assert a stale
 * figure they never looked at. That is the FIND-12 contract used as designed
 * rather than worked around by hydrating the whole record.</p>
 *
 * <p>🔴 <b>A blank FIGURE is omitted, not cleared, and this is a limitation
 * rather than a choice.</b> <c>UpdateListingRequest</c> carries exactly one clear
 * flag — <c>ClearListingAgent</c> — and the seven numeric columns are written
 * <c>request.X ?? listing.X</c> server-side, so there is no way over this wire to
 * say "erase this number". Blanking one therefore leaves the saved value where it
 * is. The modals SAY so rather than pretending otherwise; see
 * {@link sectionHasFigures}.</p>
 */
export function buildListingSectionPatch(
  section: ListingSectionConfig,
  form: ListingSectionForm,
): UpdateListingPayload {
  const patch: Record<string, unknown> = {};

  for (const field of section.fields) {
    const raw = form[field.key] ?? "";
    if (field.type === "number") {
      const parsed = parseListingNumber(raw);
      // Blank — or unparseable, which validation blocks before Save is
      // reachable — is OMITTED, which the server reads as unchanged.
      if (parsed !== undefined) patch[field.key] = parsed;
      continue;
    }
    // Strings say "clear" with "" all on their own — FIND-12 — so a blank one is
    // sent verbatim rather than dropped.
    patch[field.key] = raw.trim();
  }

  return patch as UpdateListingPayload;
}

/** Has anything in this section actually changed? Keeps Save honest. */
export function listingSectionIsDirty(
  section: ListingSectionConfig,
  listing: ListingDetailDto,
  form: ListingSectionForm,
): boolean {
  const original = hydrateListingSection(section, listing);
  return section.fields.some(
    (f) => (form[f.key] ?? "").trim() !== (original[f.key] ?? "").trim(),
  );
}
